// useSpaghettiMap.js - Custom hook for RTLS spaghetti map rendering
//
// Data flow (updated)
// ───────────────────
// rtlsFilter.js → rtlsMap.js → useSpaghettiMap.apply()
//   → resolveObjectIdsByNames() [window.Riot.Api - client-side only]
//   → fetchPositionHistory()    [dataReceiver.js → APIM → dataServe.js → RTLS API]
//   → draw Line/Sphere shapes on Riot.Map instance
//
// setupProxy.js is no longer needed for this hook.

import { useState, useCallback, useRef } from "react";
import {
  fetchPositionHistory,
  DataReceiverError,
} from "../../utils/dataReceiver";

// ─── Rendering constants ──────────────────────────────────────────────────────

const OBJECT_PALETTE = [
  [0, 160, 255, 0.85],
  [255, 72, 72, 0.85],
  [40, 210, 80, 0.85],
  [255, 180, 0, 0.85],
  [190, 70, 255, 0.85],
  [0, 210, 200, 0.85],
  [255, 120, 0, 0.85],
  [70, 170, 255, 0.85],
  [240, 50, 175, 0.85],
  [150, 215, 40, 0.85],
];

function objectColor(index) {
  return OBJECT_PALETTE[index % OBJECT_PALETTE.length];
}

// Start/end markers use fixed, universal colors (independent of the
// per-object palette above, which is only used for the path lines) so
// "start" and "end" always mean the same thing no matter which/how many
// objects are being tracked. Exported so rtlsMap.js can render a matching
// legend swatch in the status banner without duplicating the values.
export const START_MARKER_COLOR = [34, 197, 94, 1.0]; // green
export const END_MARKER_COLOR = [239, 68, 68, 1.0]; // red
export const START_MARKER_COLOR_CSS = "rgb(34, 197, 94)";
export const END_MARKER_COLOR_CSS = "rgb(239, 68, 68)";

const LINE_Z = 0.15;
const LINE_WIDTH = 6;
const START_RADIUS = 0.6;
const END_RADIUS = 0.45;
const MAX_GAP_SECONDS = 300;

// ─── Object ID resolution ─────────────────────────────────────────────────────
// Stays client-side - the backend cannot call the Kinexon WebSocket SDK.

/**
 * @param {string[]} names - Array of display names, e.g. ["Travis Nez"]
 * @returns {Promise<number[]>}
 */
function resolveObjectIdsByNames(names) {
  if (!names || names.length === 0) return Promise.resolve([]);
  try {
    const Riot = window.Riot;
    if (!Riot?.Api?.objectApi) return Promise.resolve([]);

    return Riot.Api.objectApi.getObjects().then((allObjects) => {
      const normalised = names.map((n) => n.trim().toLowerCase());
      return allObjects
        .filter(
          (o) => o.name && normalised.includes(o.name.trim().toLowerCase()),
        )
        .map((o) => o.id);
    });
  } catch (err) {
    console.warn("[useSpaghettiMap] resolveObjectIdsByNames threw:", err);
    return Promise.resolve([]);
  }
}

// ─── Data helpers ─────────────────────────────────────────────────────────────

/**
 * Group flat position rows by objectId.
 * Row format: [ts, objectId, x, y, z]
 *
 * @param {[number, number, number, number, number][]} rows
 * @returns {Map<number, { ts: number, x: number, y: number, z: number }[]>}
 */
function groupByObject(rows) {
  const groups = new Map();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const objectId = row[1];
    if (!groups.has(objectId)) groups.set(objectId, []);
    groups.get(objectId).push({ ts: row[0], x: row[2], y: row[3], z: row[4] });
  }
  groups.forEach((track) => {
    track.sort((a, b) => a.ts - b.ts);
  });
  return groups;
}

// ─── Shape helpers ────────────────────────────────────────────────────────────

/**
 * Robust shape removal - tries shape.delete() first, then map.remove().
 * @param {object} map
 * @param {object} shape
 */
function removeShape(map, shape) {
  try {
    shape.delete();
    return;
  } catch (_) {}
  try {
    if (map && typeof map.remove === "function") map.remove(shape);
  } catch (_) {}
}

/**
 * Draw a complete movement path for one tracked object onto the Riot.Map.
 * Emits: start sphere → line segments (gaps > MAX_GAP_SECONDS skipped) →
 *        final arrowhead segment → end sphere.
 *
 * @param {object}   map   - Live Riot.Map instance
 * @param {object}   Riot  - window.Riot
 * @param {{ ts: number, x: number, y: number, z: number }[]} track
 * @param {number[]} color - [r, g, b, a]
 * @param {object[]} out   - Accumulator for created shapes (for later cleanup)
 */
function drawObjectPath(map, Riot, track, color, out) {
  if (!track || track.length === 0) return;
  const [r, g, b, a] = color;

  // Start marker — fixed color (green) so "start" always reads the same
  // regardless of which object/track this is.
  try {
    const sp = track[0];
    const startMarker = new Riot.Map.Sphere({
      center: [sp.x, sp.y, LINE_Z],
      radius: START_RADIUS,
      color: START_MARKER_COLOR,
      outline: { color: [255, 255, 255, 0.9], style: "solid", width: 1 },
    });
    map.add(startMarker);
    out.push(startMarker);
  } catch (e) {
    console.warn("[useSpaghettiMap] start marker failed:", e);
  }

  // Track segments
  for (let i = 0; i < track.length - 1; i++) {
    const curr = track[i];
    const next = track[i + 1];
    if (next.ts - curr.ts > MAX_GAP_SECONDS) continue;
    try {
      const seg = new Riot.Map.Line({
        start: [curr.x, curr.y, LINE_Z],
        end: [next.x, next.y, LINE_Z],
        color: [r, g, b, a],
        width: LINE_WIDTH,
      });
      map.add(seg);
      out.push(seg);
    } catch (e) {
      console.warn(`[useSpaghettiMap] segment ${i} failed:`, e);
    }
  }

  // Final arrowhead + end marker
  try {
    const ep = track[track.length - 1];
    const prev = track.length > 1 ? track[track.length - 2] : null;

    if (prev && ep.ts - prev.ts <= MAX_GAP_SECONDS) {
      const arrow = new Riot.Map.Line({
        start: [prev.x, prev.y, LINE_Z],
        end: [ep.x, ep.y, LINE_Z],
        color: [r, g, b, 1.0],
        width: LINE_WIDTH + 1,
        arrowhead: true,
      });
      map.add(arrow);
      out.push(arrow);
    }

    // End marker — fixed color (red) so "end" always reads the same
    // regardless of which object/track this is.
    const endMarker = new Riot.Map.Sphere({
      center: [ep.x, ep.y, LINE_Z],
      radius: END_RADIUS,
      color: END_MARKER_COLOR,
      outline: { color: [255, 255, 255, 1.0], style: "solid", width: 1 },
    });
    map.add(endMarker);
    out.push(endMarker);
  } catch (e) {
    console.warn("[useSpaghettiMap] end marker failed:", e);
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useSpaghettiMap
 *
 * Full lifecycle: filter input → object ID resolution → APIM fetch →
 * track grouping → Riot.Map.Line/Sphere drawing → cleanup.
 *
 * @param {React.RefObject} riotMapRef  ref to the live Riot.Map instance
 * @returns {{
 *   isActive:    boolean,
 *   isLoading:   boolean,
 *   error:       string | null,
 *   trackCount:  number,
 *   pointCount:  number,
 *   apply:       (filters, options?) => Promise<void>,
 *   clear:       () => void,
 * }}
 *
 * filters (from rtlsFilter.js onApply):
 *   { beginTs, endTs, labels?: string[], objectNames?: string[] }
 *
 * options:
 *   { samplingPeriod = 60 }
 */
export function useSpaghettiMap(riotMapRef) {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trackCount, setTrackCount] = useState(0);
  const [pointCount, setPointCount] = useState(0);

  const shapesRef = useRef([]);
  // AbortController for in-flight APIM fetch
  const abortRef = useRef(null);

  // ── clear ──────────────────────────────────────────────────────────────
  const clear = useCallback(
    function () {
      // Cancel any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }

      const map = riotMapRef.current;
      const shapes = shapesRef.current;
      console.log(
        "[useSpaghettiMap] clear() - removing " + shapes.length + " shapes",
      );
      shapes.forEach((shape) => removeShape(map, shape));
      shapesRef.current = [];
      setIsActive(false);
      setTrackCount(0);
      setPointCount(0);
      setError(null);
    },
    [riotMapRef],
  );

  // ── apply ──────────────────────────────────────────────────────────────
  const apply = useCallback(
    function (filters, options) {
      if (!filters) filters = {};
      if (!options) options = {};

      // Validate map is ready BEFORE clearing anything
      const map = riotMapRef.current;
      if (!map) {
        console.warn("[useSpaghettiMap] apply: map not ready");
        return Promise.resolve();
      }

      const Riot = window.Riot;
      if (!Riot?.Map?.Line) {
        setError(
          "RTLS SDK not ready - wait for the map to connect before applying a spaghetti map.",
        );
        return Promise.resolve();
      }

      const beginTs = filters.beginTs;
      const endTs = filters.endTs;
      const objectNames = Array.isArray(filters.objectNames)
        ? filters.objectNames
        : [];
      const labels = Array.isArray(filters.labels) ? filters.labels : [];
      const samplingPeriod =
        options.samplingPeriod != null ? options.samplingPeriod : 60;

      if (!beginTs || !endTs) {
        setError("A date range is required to generate a spaghetti map.");
        return Promise.resolve();
      }
      if (beginTs >= endTs) {
        setError("'From' date must be earlier than 'To' date.");
        return Promise.resolve();
      }

      setIsLoading(true);
      setError(null);
      // Clear existing shapes before async work begins
      clear();

      // Fresh AbortController for this fetch lifecycle
      const abortController = new AbortController();
      abortRef.current = abortController;

      return resolveObjectIdsByNames(objectNames)
        .then(function (objectIds) {
          // Guard: names were requested but none resolved - surface a clear error
          if (objectNames.length > 0 && objectIds.length === 0) {
            throw new Error(
              'No RTLS objects matched "' +
                objectNames.join('", "') +
                '". Verify the object name matches exactly in the RTLS system.',
            );
          }

          if (objectIds.length > 0) {
            console.log(
              "[useSpaghettiMap] Resolved IDs: [" + objectIds.join(", ") + "]",
            );
          }

          // ── Fetch via dataReceiver → APIM ─────────────────────────────
          console.log("[useSpaghettiMap] Fetching position history via APIM…", {
            beginTs,
            endTs,
            objectIds,
            labels,
            samplingPeriod,
          });

          return fetchPositionHistory(
            { beginTs, endTs, objectIds, labels, samplingPeriod },
            abortController.signal,
          );
        })
        .then(function (json) {
          // Guard: abort fired during fetch
          if (abortController.signal.aborted) return;

          if (!json.data || json.data.length === 0) {
            setError(
              "No position history found for this filter. " +
                "Try a wider date range or verify the tracker was active.",
            );
            setIsLoading(false);
            return;
          }

          console.log(
            "[useSpaghettiMap] Received " +
              json.data.length +
              " position points.",
          );

          // Re-read map ref AFTER the async fetch in case of re-render
          const currentMap = riotMapRef.current;
          if (!currentMap) {
            console.warn(
              "[useSpaghettiMap] map became unavailable during fetch - aborting draw",
            );
            setIsLoading(false);
            return;
          }

          const currentRiot = window.Riot;
          if (!currentRiot?.Map?.Line) {
            setError("RTLS SDK became unavailable during fetch.");
            setIsLoading(false);
            return;
          }

          const tracks = groupByObject(json.data);
          console.log(
            "[useSpaghettiMap] Drawing " + tracks.size + " track(s).",
          );

          const newShapes = [];
          let colorIndex = 0;
          tracks.forEach(function (track, objectId) {
            const color = objectColor(colorIndex++);
            console.log(
              "[useSpaghettiMap] Object " +
                objectId +
                ": " +
                track.length +
                " points.",
            );
            drawObjectPath(currentMap, currentRiot, track, color, newShapes);
          });

          shapesRef.current = newShapes;
          setIsActive(true);
          setTrackCount(tracks.size);
          setPointCount(json.data.length);
          console.log(
            "[useSpaghettiMap] Done - " +
              newShapes.length +
              " shapes across " +
              tracks.size +
              " track(s).",
          );
        })
        .catch(function (err) {
          // Ignore abort cancellations
          if (err.name === "AbortError") return;

          console.error("[useSpaghettiMap] apply error:", err);
          setError(
            err instanceof DataReceiverError
              ? err.message
              : err.message || "Failed to load position history.",
          );
          clear();
        })
        .finally(function () {
          // Only clear loading if this controller is still active
          if (abortRef.current === abortController) {
            setIsLoading(false);
            abortRef.current = null;
          }
        });
    },
    [riotMapRef, clear],
  );

  return { isActive, isLoading, error, trackCount, pointCount, apply, clear };
}
